import BaseUISystem from "../BaseUISystem";
import Point from "Utility/Point";
import { PopupID } from "State/UI/PopupID";
import { FarmMiniGameLayout } from "Layouts/Farm/FarmMiniGameLayout";
import RelativePositionComponent from "Component/Render/RelativePosition";
import BaseMenu from "System/UI/Menu/BaseMenu";
import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import Armature from "Prototype/Armature";
import SpriteComponent from "Component/Render/Sprite";
import { ArrayHelper } from "Utility/ArrayHelper";
import { Anim, StartAnimationClip } from "System/Animation/AnimationHelper";
import { Alpha, DragonbonesDefault, DragonbonesFadeIn } from "System/Animation/Clip";
import Interactable from "Component/Interactable";
import TextComponent from "Component/Render/Text";
import Pet from "Prefabs/Pet/Pet";
import { PetFoodData, PetFoodType, PetId } from "Constants/Pets";
import { PetAnimations } from "Utility/Helpers/Pets/PetAnimations";
import { PetArmature } from "Utility/Helpers/Pets/PetArmature";
import { UIActions } from "State/UI/UIActions";
import Game from "Game";
import { Random } from "Utility/Random";
import LevelHelper from "Utility/LevelHelper";
import { boosterTypeByRewardType, getRewardData, IRewardData, RewardType } from "Utility/RewardHelper";
import { unlockLevelByPreBoosterType } from "./LevelPopup/LevelPopupBooster";
import { isPreBoosterType } from "State/Card/BoosterType";
import { FarmActions } from "State/Farm/FarmActions";
import { PlaySound } from "System/Audio/AudioHelper";
import { PetsHelper } from "Utility/Helpers/Pets/PetsHelper";

const DATA_KEY = 'farmMiniGameRewards';

type Data = {
    prizeWeights: Random.IWeightedItem<IRewardData[]>[];
    overridesForLockedReward: Record<RewardType, IRewardData>;
}

// TODO: Make dynamic to support different pets.
const PET_DRAGONBONES_ASSET = `my_farm_minigame_animations_${PetId.Charly}`;

export default class FarmMiniGameMenuHandler extends BaseMenu {
    private layout: any;
    private pet: Pet;
    private hovers: Map<number, boolean>;

    constructor(props: { system: BaseUISystem }) {
        super(PopupID.FARM_MINI_GAME, props.system, false);
    }

    public async preload() {
        Game.resource.add(DATA_KEY, 'resource/data/rewards/farmMiniGameRewards.json');

        // Cannot return Promise.all due to `preload()` return type is strictly `Promise<void>`.
        await Promise.all([

            // Ensure pet animations are loaded for the menu as clicking the food items before the `Pet` prototype is loaded could cause an error.
            PetsHelper.loadPetAnimations(PetId.Charly, PET_DRAGONBONES_ASSET),

            // Await standard resource loading.
            super.preload()
        ]);
    }

    public onCreate() {
        super.onCreate();

        this.hovers = new Map();

        this.layout = new FarmMiniGameLayout();
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0, 0);
        this.element.addChild(this.layout.root);

        // Add Pet to mini game UI
        this.pet = GameObject.Instantiate(Pet, {
            petId: PetId.Charly,
            miniGame: true,
            armatureWhitelist: [PetArmature.MINI_GAME_FED, PetArmature.MINI_GAME_HUNGRY],
            dragonbonesName: PET_DRAGONBONES_ASSET
        });
        this.pet.getComponent(Interactable).update({ buttonMode: false, enabled: false});
        this.layout.petContainer.addChild(this.pet);

        // Initialize pet with needed animation
        this.pet.initialized.then(() => {
            // TODO: implement correct animation based on timer
            return this.pet.play(PetAnimations.MINI_GAME_HUNGRY);
        });
    }

    public onShow(params: any) {
        super.onShow(params);

        let { prizeWeights, levelEntry } = this.getRewardsData();
        const shuffledArray = ArrayHelper.shuffle(Object.values(PetFoodType));

        this.layout.foodContainer.children.forEach((foodObject:any, index:number) => {
            const foodData = PetFoodData[shuffledArray[index]];
            const food = this.layout.foodContainer.food[index];

            food.foodSprite.getComponent(SpriteComponent).texture = foodData.sprite;

            const baseRewards = Random.getRandomWeighted(prizeWeights);
            // Filter out reward from pool to avoid duplicates
            prizeWeights = prizeWeights.filter(r => r.data !== baseRewards);

            // Adjust coin reward value by levelCost
            const calculatedRewards = baseRewards.map((rewardData) => {
                return {
                    ...rewardData,
                    value: rewardData.type === RewardType.COINS ? rewardData.value * levelEntry : rewardData.value
                }
            });

            const displayReward = calculatedRewards[0];
            const rewardSpriteData = getRewardData(displayReward.type);

            food.rewardSprite.getComponent(SpriteComponent).texture = rewardSpriteData.texture;
            food.rewardText.getComponent(TextComponent).text = `x${displayReward.value}`;

            const onSelected = () => {
                this.hovers.set(index, false);
                this.updateHover();
                foodObject.off(GameObjectEvent.POINTER_OVER);
                foodObject.off(GameObjectEvent.POINTER_OUT);
                this.handleFoodClick(food, foodData.armatureName, calculatedRewards)
            }

            const onPointerOver = () => {
                this.hovers.set(index, true);
                this.updateHover();
            }

            const onPointerOut = () => {
                this.hovers.set(index, false);
                this.updateHover();
            }

            foodObject.on(GameObjectEvent.SELECTED, onSelected);
            foodObject.on(GameObjectEvent.POINTER_OVER, onPointerOver);
            foodObject.on(GameObjectEvent.POINTER_OUT, onPointerOut);

            this.cleanupManager.add(() => {
                foodObject.off(GameObjectEvent.SELECTED, onSelected);
                foodObject.off(GameObjectEvent.POINTER_OVER, onPointerOver);
                foodObject.off(GameObjectEvent.POINTER_OUT, onPointerOut);
            })
        });
    }

    private readonly updateHover = () => {
        this.layout.foodContainer.children.forEach((foodObject:any, index:number) => {
            const scale = this.hovers.get(index) ? 1.1 : 1;

            Anim(foodObject, {
                scaleX: scale,
                scaleY: scale,
                duration: 100,
            });
        });
    };

    private getRewardsData(): {
        levelEntry: number;
        prizeWeights: Random.IWeightedItem<IRewardData[]>[];
    } {
        const rewardsData: Data = Game.resource.get(DATA_KEY);
        const levelEntry = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted());
        let prizeWeights = rewardsData.prizeWeights;

        const isLocked = (type: RewardType): boolean => {
            const boosterType = boosterTypeByRewardType[type];

            if (isPreBoosterType(boosterType)) {
                return Game.player.getHighestLevelCompleted() + 1 < unlockLevelByPreBoosterType[boosterType];
            }

            return false;
        };

        prizeWeights = prizeWeights.map((weight) => {
            return {
                odds: weight.odds,
                data: weight.data.map((p) => {
                    const override = rewardsData.overridesForLockedReward[p.type];
                    return isLocked(p.type) && override ? override : p;
                }),
            };
        });

        return { levelEntry, prizeWeights };
    }

    /**
     * TODO: Refactor reward calculation and non-deterministic minigame steps into Redux actions and add relevant state listeners.
     * @param selectedFood
     * @param foodArmatureName
     * @param rewards
     * @private
     */
    private handleFoodClick(selectedFood: any, foodArmatureName: string, rewards: IRewardData[]): void {

        FarmActions.receiveMiniGameReward(rewards);

        const petsState = Game.store.getState().playerState.petsState;
        const petId = petsState.activePet;
        // Get all the foods
        const foods = this.layout.foodContainer.children;
        // Disable interactivity and remove listeners
        foods.forEach(
          (food:any) => {
                food.getComponent(Interactable).update({
                    buttonMode: false,
                    enabled: false
                });
            }
        );

        // Create and add db armature
        const foodArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: PET_DRAGONBONES_ASSET,
            armatureName: foodArmatureName,
            animationName: 'animation',
            playOnStart: false,
            x: 285,
            y: 42,
        });
        this.layout.petContainer.addChild(foodArmature);

        // Add rays animation too selected food
        const raysArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: PET_DRAGONBONES_ASSET,
            armatureName: 'reward_rays',
            animationName: 'idle',
            playOnStart: true,
        });
        selectedFood.DBContainer.addChild(raysArmature);

        PlaySound(`${petId}_eating`);

        StartAnimationClip(
            new DragonbonesFadeIn(
                raysArmature,
                {
                    fadeInTime: 500
                }
            )
        );

        // Add sparks animation too selected food
        const sparksArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: PET_DRAGONBONES_ASSET,
            armatureName: 'reward_sparks',
            animationName: 'animation',
            playOnStart: false,
        });
        selectedFood.rewardContainer.addChild(sparksArmature);

        // Show selected food selector
        StartAnimationClip(
            new Alpha(
                selectedFood.selectorSprite, {
                startAlpha: 0,
                endAlpha: 1,
                duration: 400,
            })
        );

        // Show selected food check
        Anim(selectedFood.checkSprite, { alpha: 1, duration: 400 })
            .then(() => {
                // Hide selected food sprite
                StartAnimationClip(
                    new Alpha(
                        selectedFood.spritesContainer, {
                        startAlpha: 1,
                        endAlpha: 0,
                        duration: 400,
                    })
                );

                // Reveal selected reward
                StartAnimationClip(
                    new Alpha(
                        selectedFood.rewardContainer, {
                        startAlpha: 0,
                        endAlpha: 1,
                        duration: 400,
                    })
                );

                // Show other food rewards with 0.5 alpha
                foods.forEach((food:any, index:number) => {
                    const selectedFoodContainerId = selectedFood.children[0].parent.id;
                    if (food.id === selectedFoodContainerId) return;
                    const foodLayout = this.layout.foodContainer.food[index];
                    StartAnimationClip(
                        new Alpha(
                            foodLayout.rewardContainer, {
                            startAlpha: 0,
                            endAlpha: 0.5,
                            duration: 400,
                        })
                    );
                    StartAnimationClip(
                        new Alpha(
                            foodLayout.spritesContainer, {
                            startAlpha: 0,
                            endAlpha: 0.5,
                            duration: 400,
                        })
                    )
                });

                // Show rays animation
                StartAnimationClip(
                    new Alpha(
                        raysArmature, {
                        startAlpha: 0,
                        endAlpha: 1,
                        duration: 400,
                    })
                );

                // Show sparks animation once
                StartAnimationClip(
                    new DragonbonesFadeIn(
                        sparksArmature,
                        {
                            playTimes: 1,
                            fadeInTime: 400
                        }
                    )
                );
            }).then(() => {
                // Show food being eaten once
                StartAnimationClip(
                    new DragonbonesDefault(foodArmature, {
                        playTimes: 1,
                    })
                );

                // Animate pet to fed state
                this.pet.play(PetAnimations.MINI_GAME_FED);

                // Show continue text
                StartAnimationClip(
                    new Alpha(
                        this.layout.continueText, {
                        startAlpha: 0,
                        endAlpha: 1,
                        duration: 400,
                    })
                );

                // Add listener to overlay to close popup
                this.system.overlayPopup.once(GameObjectEvent.SELECTED, () => {
                    UIActions.hidePopup(PopupID.FARM_MINI_GAME);
                    this.system.scene.emit('PetSleeping');
                });
            });
    }
}
